import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import DeleteIcon from 'assets/deleteIcon.svg?react';
import PDFIcon from 'assets/pdf.svg?react';
import DuplicateIcon from 'assets/file_copy_icon.svg?react';
import AddIcon from 'assets/add.svg?react';
import { useAuth } from 'utilities/base-auth';
import useApplication from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import useWorkOrders from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useWorkOrders';
import { message } from 'antd';
import applicationNotification from 'screens/Applications&WorkOrders/Applications/Components/Notification';
import workOrderNotification from 'screens/Applications&WorkOrders/WorkOrders/Components/Notification';
import SpinnerModal from 'components/SpinnerModal';
import APPLICATION_NOTIFICATION_STRINGS from 'screens/Applications&WorkOrders/Applications/Components/Notification/Constants/notificationString';
import WORK_ORDER_NOTIFICATION_STRINGS from 'screens/Applications&WorkOrders/WorkOrders/Components/Notification/Constants/notificationString';
import { paths } from 'routes/paths';
import { ExportLanguageEnum } from 'screens/Applications&WorkOrders/Hooks/useOperationPdf';
import { RECORD_SOURCE_MESSAGE_KEY } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/constants/messageKey';
import { Flow } from 'screens/Applications&WorkOrders/RouteAdmin';
import { OperationType as OperationTypeEnum } from 'components/RecordsListView/constants';
import useOperationEvents from 'hooks/useOperationEvents';
import {
  getSelectedCropSeasonIds,
  shouldShowOperationCropSeasonMismatch
} from 'helpers/cropSeasonMismatch';
import showCropSeasonMismatchModal from 'helpers/showCropSeasonMismatchModal';
import { OperationDetails } from '../types';
import PdfSettingsModal from './components/PdfSettingsModal';
import showDeleteConfirmModal from './components/DeleteConfirmModal';
import './index.css';
import EditButton from './components/EditButton';
import DropdownMenu, { MenuItem } from './components/DropdownMenu';
import usePrintSettingsModal from './hooks/usePrintSettingsModal';
import { createDuplicateLocationState } from './helpers';

interface ActionMenuProps {
  operation?: OperationDetails; // Application or Work Order data
  operationType: OperationTypeEnum; // Type of operation to determine available actions
  onEdit?: () => void; // Callback when edit is clicked
  onDelete?: (deletedId: string) => void; // Callback after successful deletion
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  operation,
  operationType,
  onEdit,
  onDelete
}) => {
  const history = useHistory();
  const {
    sendInitiateEditEvent,
    sendInitiateWorkOrderToApplicationEvent,
    sendInitiateDuplicateEvent,
    sendDeleteEvent
  } = useOperationEvents(operationType);
  const {
    user: { id: userId }
  } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);

  const { deleteApplication } = useApplication();
  const { deleteWorkOrder } = useWorkOrders();

  const isApplication = operationType === OperationTypeEnum.APPLICATION;

  const deleteOperation = isApplication ? deleteApplication : deleteWorkOrder;
  const notification = isApplication
    ? applicationNotification
    : workOrderNotification;

  // Use print settings hook for all print-related logic
  const {
    isExportModalOpen,
    setIsExportModalOpen,
    orientation,
    setOrientation,
    onOpenExportModal,
    handleExport,
    handlePrint,
    downloadingPDF
  } = usePrintSettingsModal({
    operation,
    operationType: isApplication ? 'application' : 'work-order',
    userId
  });

  const handleEdit = () => {
    if (!operation) return;

    sendInitiateEditEvent();

    const location = isApplication
      ? `${paths.applications}/${operation.id}/edit`
      : `${paths.workOrders}/${operation.id}/edit`;

    const navigateToEdit = () => {
      history.push(location, {
        operation,
        flow: Flow.EDIT
      });

      onEdit?.();
    };

    const selectedCropSeasonIds = getSelectedCropSeasonIds();
    if (
      shouldShowOperationCropSeasonMismatch(operation, selectedCropSeasonIds)
    ) {
      showCropSeasonMismatchModal(navigateToEdit, operationType, undefined, {
        actionType: 'edit'
      });
      return;
    }

    navigateToEdit();
  };

  const handleCreateApplication = () => {
    sendInitiateWorkOrderToApplicationEvent();

    history.push(`${paths.applications}/create`, {
      flow: Flow.FROM_WORK_ORDER_TO_APPLICATION,
      operation,
      returnPath: paths.workOrders
    });
  };

  const handleDuplicate = () => {
    if (!operation) return;

    sendInitiateDuplicateEvent('Detail View menu');

    const location = isApplication
      ? `${paths.applications}/create`
      : `${paths.workOrders}/create`;

    const locationState = createDuplicateLocationState(
      operation,
      operationType
    );

    const navigateToDuplicate = () => {
      history.push(location, locationState);
    };

    const selectedCropSeasonIds = getSelectedCropSeasonIds();
    if (
      shouldShowOperationCropSeasonMismatch(operation, selectedCropSeasonIds)
    ) {
      showCropSeasonMismatchModal(
        navigateToDuplicate,
        operationType,
        undefined,
        {
          actionType: 'duplicate'
        }
      );
      return;
    }

    navigateToDuplicate();
  };

  const handleDelete = () => {
    if (!operation || !deleteOperation) return;

    showDeleteConfirmModal(operationType, async () => {
      setIsDeleting(true);
      const deleteStartedAt = performance.now();
      try {
        await deleteOperation(operation.id);
        const deleteApiLengthMs = Math.round(
          performance.now() - deleteStartedAt
        );

        sendDeleteEvent('Success', deleteApiLengthMs);

        // Notify parent that deletion was successful
        onDelete?.(operation.id);
        // Show success notification
        notification('DELETE_SUCCESS');
      } catch (e) {
        const deleteApiLengthMs = Math.round(
          performance.now() - deleteStartedAt
        );

        sendDeleteEvent('Error', deleteApiLengthMs, e);

        // Show error notification
        notification('DELETE_ERROR');
      } finally {
        setIsDeleting(false);
      }
    });
  };

  const onClickWrapper = (actionOnClick?: () => void) => () => {
    message.destroy(RECORD_SOURCE_MESSAGE_KEY); // Clear any existing messages before performing the action
    if (actionOnClick) {
      actionOnClick();
    }
  };

  // Build menu items based on operation type
  const isWorkOrder = !isApplication;
  const menuItems: MenuItem[] = [
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <DuplicateIcon style={{ width: '18px', height: '18px' }} />,
      textColor: '#1F2937',
      hoverBgColor: '#F3F4F6',
      onClick: onClickWrapper(handleDuplicate)
    },
    {
      key: 'export-pdf-english',
      label: 'Create PDF (English)',
      icon: <PDFIcon style={{ width: '18px', height: '18px' }} />,
      textColor: '#1F2937',
      hoverBgColor: '#F3F4F6',
      onClick: onClickWrapper(() => {
        onOpenExportModal(ExportLanguageEnum.ENGLISH);
      })
    },
    ...(isWorkOrder
      ? [
          {
            key: 'export-pdf-spanish',
            label: 'Create PDF (Spanish)',
            icon: <PDFIcon style={{ width: '18px', height: '18px' }} />,
            textColor: '#1F2937',
            hoverBgColor: '#F3F4F6',
            onClick: onClickWrapper(() => {
              onOpenExportModal(ExportLanguageEnum.LATAM_SPANISH);
            })
          },
          {
            key: 'create-application',
            label: 'Create Application',
            icon: <AddIcon style={{ width: '18px', height: '18px' }} />,
            textColor: '#1F2937',
            hoverBgColor: '#F3F4F6',
            onClick: onClickWrapper(handleCreateApplication)
          }
        ]
      : []),
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIcon style={{ width: '18px', height: '18px' }} />,
      textColor: '#CF3537',
      hoverBgColor: '#FFF1F1',
      onClick: onClickWrapper(handleDelete)
    }
  ];

  return (
    <div className="flex items-center gap-2">
      {/* Edit Button */}
      <EditButton onEdit={handleEdit} />

      {/* More Actions Dropdown Menu - Only show if there are menu items */}
      {menuItems.length > 0 && <DropdownMenu items={menuItems} />}

      {/* PDF Settings Modal */}
      <PdfSettingsModal
        open={isExportModalOpen}
        orientation={orientation}
        onOrientationChange={setOrientation}
        onDownload={handleExport}
        onPrint={handlePrint}
        onCancel={() => setIsExportModalOpen(false)}
        isLoading={downloadingPDF}
      />

      {/* Spinner Modals */}
      <SpinnerModal
        open={isDeleting}
        message={
          isApplication
            ? APPLICATION_NOTIFICATION_STRINGS.DELETING_APPLICATION_MESSAGE
            : WORK_ORDER_NOTIFICATION_STRINGS.DELETING_WORK_ORDER_MESSAGE
        }
      />
      <SpinnerModal
        open={downloadingPDF}
        message={
          isApplication
            ? APPLICATION_NOTIFICATION_STRINGS.DOWNLOADING_PDF_MESSAGE
            : WORK_ORDER_NOTIFICATION_STRINGS.DOWNLOADING_PDF_MESSAGE
        }
      />
    </div>
  );
};

export default ActionMenu;
